import React, {useState, useEffect, useMemo, useCallback} from "react";

// Hooks
import {useFetchVideosBySeason} from "../../../../../Hooks/Content/Watch/VideoFeed/useFetchVideosBySeason";

// Components
import SelectInput from "../../../../Inputs/SelectInput/SelectInput";
import ErrorMessage from "../../../../Text/ErrorMessage";
import VideoContentGridLandscape from "../VideoFeed/VideoContentGridLandscape";

// Assets
import LoaderCircle from "../../../../../Assets/SVGs/Icons/Loading/LoaderCircle";
import IconLoaderCircle from "../../../../../Assets/SVGs/Icons/Loading/LoaderCircle";

// Models
import ICarouselContentDTO from "../../../../../Models/DTOs/ICarouselContentDTO";

// Helpers
import {transformVideoToCarouselContent} from "../../../../../Helpers/DataConversion/ConvertToCarouselContentItem";
import {createPlaceholderICarouselContentDTO} from "../../../../../Helpers/Content/CarouselContent";


interface Props {
    seriesId: number;
    yearly: boolean;
    seasonCount: number;
    seriesDatetime: string | null;
    isDarkMode: boolean;
}

interface OptionType {
    value: number;
    label: string | React.ReactNode;
}

function getBaseYear(seriesDatetime: string | null): number | null {
    if (!seriesDatetime) return null;
    const d = new Date(seriesDatetime);
    const y = d.getUTCFullYear();
    return Number.isFinite(y) ? y : null;
}

const SeasonSelector: React.FC<Props> = (
    {
        seriesId,
        yearly,
        seasonCount,
        seriesDatetime,
        isDarkMode,
    }) => {
    const
        baseYear = useMemo(() => getBaseYear(seriesDatetime), [seriesDatetime]),

        buildLabelForKey = useCallback(
            (seasonKey: number) => {
                if (!yearly) return `Season ${seasonKey}`;

                // Season 1 == baseYear, Season N == baseYear + (N-1)
                if (baseYear == null) return `Year ${seasonKey}`;

                const year = baseYear + (seasonKey - 1);
                return `${year}`;
            },
            [yearly, baseYear]
        ),

        [selectedKey, setSelectedKey] = useState<number | null>(null),

        {active, loading, error, loadingMap} = useFetchVideosBySeason(
            seriesId,
            yearly,
            seasonCount,
            selectedKey,
            buildLabelForKey,
            {take: 25, apiDelay: 150} // tweak take as you like
        ),

        seasonKeysInOrder = useMemo(() => {
            if (!seasonCount || seasonCount < 1) return [];
            const keys: number[] = [];
            if (yearly) {
                for (let k = seasonCount; k >= 1; k--) keys.push(k);
            } else {
                for (let k = 1; k <= seasonCount; k++) keys.push(k);
            }
            return keys;
        }, [seasonCount, yearly]),

        options = useMemo<OptionType[]>(() => {
            return seasonKeysInOrder.map((k) => ({
                value: k,
                label: (
                    <div className="season-selector__option">
                        <span>{buildLabelForKey(k)}</span>
                        {loadingMap[k] && <LoaderCircle iconFill="#ddd"/>}
                    </div>
                ),
            }));
        }, [seasonKeysInOrder, buildLabelForKey, loadingMap]),

        handleSeasonChange = useCallback(
            (e: { target: { name: string; value: any } }) => {
                const next = Number(e.target.value);
                if (!Number.isFinite(next)) return;
                if (next < 1 || next > seasonCount) return;
                setSelectedKey(next);
            },
            [seasonCount]
        ),

        activeEpisodes = useMemo(() => {
            if (!active || selectedKey == null) return undefined;

            const isStillLoading = !!loadingMap[selectedKey];

            const transformed: ICarouselContentDTO[] = (active.episodes ?? []).map(transformVideoToCarouselContent);

            const placeholders: ICarouselContentDTO[] =
                isStillLoading ? (createPlaceholderICarouselContentDTO(10) as ICarouselContentDTO[]) : [];

            return [...transformed, ...placeholders];
        }, [active, selectedKey, loadingMap]);

    // Default selection & validation
    useEffect(() => {
        if (!seasonCount || seasonCount < 1) {
            setSelectedKey(null);
            return;
        }

        const desiredDefault = yearly ? seasonCount : 1;

        if (selectedKey == null || selectedKey < 1 || selectedKey > seasonCount) {
            setSelectedKey(desiredDefault);
        }
    }, [seasonCount, yearly, selectedKey]);

    if (error) return (
        <div className="layout layout--season-selector">
            <ErrorMessage errorMessage="Loading season failed." />
        </div>
    );

    return (
        <div className="layout layout--season-selector">
            <div className={`season-selector__select-wrapper ${loading ? "is-loading" : "is-not-loading"}`}>
                {options.length === 1 && !loading ? (
                    <h2 className="season-selector__single-season">{options[0].label}</h2>
                ) : (
                    <SelectInput
                        name="season"
                        value={selectedKey}
                        options={options}
                        placeholder={yearly ? "Select Year…" : "Select Season…"}
                        onChange={handleSeasonChange}
                    />
                )}

                {IconLoaderCircle({
                    className: `season-selector__loading-circle ${loading ? "is-loading" : "is-not-loading"}`,
                    iconFill: "#ddd",
                })}
            </div>

            <VideoContentGridLandscape
                isDarkMode={isDarkMode}
                carouselItems={activeEpisodes}
                customLoadingItemCount={25}
                isLoading={loading}
            />
        </div>
    );
};

export default SeasonSelector;
